import ReactGA from "react-ga4";

/**
 * RouteTracker owns pageviews. Also disable "Page changes based on browser
 * history events" in the GA4 web stream; send_page_view only controls startup.
 * See docs/plans/2026-09-14-monetization-pilot.md for release verification.
 */
export function initializeAnalytics(trackingId: string | undefined, stage: string) {
  if (!trackingId || stage === "local") return;

  ReactGA.initialize(trackingId, {
    testMode: stage === "development",
    gtagOptions: { send_page_view: false },
  });
}

/**
 * Analytics event taxonomy.
 *
 * **Convention:** `{domain}_{action}[_outcome]`
 *
 * - `domain` — the user-facing surface (`region`, `camera`, `multicam`,
 *   `report`, `report_form`, `auth`, `settings`, `wind_map`, `welcome`,
 *   `home_spot`, `share`, `nav`, `support`).
 * - `action` — a single verb describing what the user did.
 * - `outcome` — `success` | `error` | `cancel`. Added **only** when an
 *   action has a meaningful attempt / result distinction. Instant,
 *   non-failable actions (opening a drawer, scrubbing a slider) stay
 *   outcome-free.
 *
 * **Why outcomes matter:** GA4 funnels split on them. We need
 * `auth_signin_click → auth_signin_success` as two separate events to
 * measure sign-in drop-off.
 *
 * **Params** use `snake_case` keys. Prefer IDs and enum-like values over
 * free text so GA4's explorer stays tidy.
 *
 * ## Catalogue
 *
 * | Event | Fires when | Params |
 * |---|---|---|
 * | `region_change` | active region switches | `region_id` |
 * | `zone_expand` | country/zone row is expanded in the drawer | `zone` |
 * | `camera_change` | active camera switches | `camera_name`, `region_id` |
 * | `camera_stream_success` | HLS manifest parsed, video playable | `camera_name` |
 * | `camera_stream_error` | HLS fatal error | `camera_name`, `reason` |
 * | `multicam_open` | enter multi-cam view | `camera_count` |
 * | `multicam_add` | camera added to grid | — |
 * | `multicam_remove` | camera removed from grid | — |
 * | `nav_drawer_open` | hamburger menu opens | — |
 * | `settings_unit_change` | unit system toggled | `system` |
 * | `wind_map_scrub` | time slider dragged | `forecast_hour` |
 * | `auth_signin_click` | sign-in button pressed | `source` |
 * | `auth_signin_success` | Firebase auth state flips to authed | — |
 * | `auth_signin_error` | auth popup/redirect rejects | `reason` |
 * | `auth_signout_success` | sign-out completes | — |
 * | `report_form_open` | report form dialog opens | `mode` |
 * | `report_form_cancel` | dialog closed without submitting | — |
 * | `report_submit_success` | new report saved | `region_id`, `wave_size` |
 * | `report_submit_error` | save fails | `reason` |
 * | `report_edit_success` | edit saved | — |
 * | `report_delete_success` | delete confirmed | — |
 * | `report_vote` | confirm / deny cast | `vote_type` |
 * | `report_like_add` | like added | — |
 * | `report_like_remove` | like removed | — |
 * | `home_spot_set` | star clicked, home spot set | `region_id`, `camera_id?` |
 * | `home_spot_clear` | star clicked, home spot cleared | — |
 * | `share_click` | share button pressed | — |
 * | `share_success` | native share or clipboard copy succeeds | `method` |
 * | `share_cancel` | user dismisses share sheet | — |
 * | `welcome_arrival` | `/welcome` mounts | `campaign` |
 * | `welcome_locate_success` | geolocation → region | `region_id`, `campaign` |
 * | `welcome_locate_error` | geolocation fails | `reason`, `campaign` |
 * | `welcome_signin_click` | sign-in pressed on welcome | `campaign` |
 * | `welcome_skip_click` | "browse without signing in" pressed | `campaign` |
 * | `engagement_prompt_shown` | anon visitor hits the N-unique-region threshold | — |
 * | `engagement_prompt_dismissed` | engagement prompt closed without signin | — |
 * | `support_prompt_shown` | support dialog becomes visible (exposure) | `source` |
 * | `support_click` | visitor follows an existing support link | `provider`, `source` |
 *
 * ## Not tracked (intentional)
 *
 * - Route-level pageviews — handled by `components/RouteTracker.tsx`
 *   via `ReactGA.send({ hitType: "pageview" })`. No need for a peer
 *   `navigate` event.
 * - Scroll depth, time-on-page — GA4 provides enhanced measurement.
 *
 * ## Gaps acknowledged but deferred
 *
 * - **Media upload success/error** on `ReportFormDialog` — small surface,
 *   add when we revisit the report form.
 * - **Forecast detail-panel date select / tide chart hover** — the
 *   components don't currently emit interaction handlers; instrumenting
 *   them is non-trivial and lower-value than the gaps filled here.
 */

/** Thin wrapper around `ReactGA.event` for consistent event tracking. */
export function trackEvent(
  action: string,
  params?: Record<string, string | number | boolean>,
) {
  ReactGA.event(action, params);
}

// A prompt impression is not intent, and an outbound click is not a payment.
export type SupportSource = "camera_switch_prompt" | "manual";

export const trackSupportPromptShown = (source: SupportSource) =>
  trackEvent("support_prompt_shown", { source });

export const trackSupportClick = (
  provider: "buy_me_a_coffee" | "paypal",
  source: SupportSource,
) => trackEvent("support_click", { provider, source });

// ── Region & Navigation ──────────────────────────────────────────────────

export const trackRegionChange = (regionId: string) =>
  trackEvent("region_change", { region_id: regionId });

export const trackZoneExpand = (zone: string) => trackEvent("zone_expand", { zone });

export const trackNavDrawerOpen = () => trackEvent("nav_drawer_open");

// ── Camera ───────────────────────────────────────────────────────────────

export const trackCameraChange = (cameraName: string, regionId: string) =>
  trackEvent("camera_change", { camera_name: cameraName, region_id: regionId });

export const trackCameraStreamSuccess = (cameraName: string) =>
  trackEvent("camera_stream_success", { camera_name: cameraName });

export const trackCameraStreamError = (cameraName: string, reason: string) =>
  trackEvent("camera_stream_error", { camera_name: cameraName, reason });

export const trackMultiCamOpen = (cameraCount: number) =>
  trackEvent("multicam_open", { camera_count: cameraCount });

export const trackMultiCamAdd = () => trackEvent("multicam_add");

export const trackMultiCamRemove = () => trackEvent("multicam_remove");

// ── Reports ──────────────────────────────────────────────────────────────

export const trackReportFormOpen = (mode: "create" | "edit") =>
  trackEvent("report_form_open", { mode });

export const trackReportFormCancel = () => trackEvent("report_form_cancel");

export const trackReportSubmitSuccess = (regionId: string, waveSize: string) =>
  trackEvent("report_submit_success", { region_id: regionId, wave_size: waveSize });

export const trackReportSubmitError = (reason: string) =>
  trackEvent("report_submit_error", { reason });

export const trackReportEditSuccess = () => trackEvent("report_edit_success");

export const trackReportDeleteSuccess = () => trackEvent("report_delete_success");

export const trackReportVote = (voteType: "confirm" | "deny") =>
  trackEvent("report_vote", { vote_type: voteType });

export const trackReportLikeAdd = () => trackEvent("report_like_add");

export const trackReportLikeRemove = () => trackEvent("report_like_remove");

// ── Auth ─────────────────────────────────────────────────────────────────

/** `source` identifies where the sign-in UI was surfaced (nav, prompt, welcome). */
export const trackAuthSigninClick = (source: string) =>
  trackEvent("auth_signin_click", { source });

export const trackAuthSigninSuccess = () => trackEvent("auth_signin_success");

export const trackAuthSigninError = (reason: string) =>
  trackEvent("auth_signin_error", { reason });

export const trackAuthSignoutSuccess = () => trackEvent("auth_signout_success");

// Email/password auth — tracked separately from Google so the funnel
// can split by provider and surface the friction points (wrong password,
// weak password, forgot-password round-trips, signup → verify).
export const trackAuthEmailSigninAttempt = (source: string) =>
  trackEvent("auth_email_signin_attempt", { source });

export const trackAuthEmailSigninSuccess = (source: string) =>
  trackEvent("auth_email_signin_success", { source });

export const trackAuthEmailSigninError = (source: string, reason: string) =>
  trackEvent("auth_email_signin_error", { source, reason });

export const trackAuthEmailSignupAttempt = (source: string) =>
  trackEvent("auth_email_signup_attempt", { source });

export const trackAuthEmailSignupSuccess = (source: string) =>
  trackEvent("auth_email_signup_success", { source });

export const trackAuthEmailSignupError = (source: string, reason: string) =>
  trackEvent("auth_email_signup_error", { source, reason });

export const trackAuthPasswordResetRequest = (source: string) =>
  trackEvent("auth_password_reset_request", { source });

export const trackAuthEmailVerificationSent = (source: string) =>
  trackEvent("auth_email_verification_sent", { source });

export const trackAuthFormModeToggle = (to: "signin" | "signup") =>
  trackEvent("auth_form_mode_toggle", { to });

// ── Settings ─────────────────────────────────────────────────────────────

export const trackSettingsUnitChange = (system: string) =>
  trackEvent("settings_unit_change", { system });

// ── Wind Map ─────────────────────────────────────────────────────────────

export const trackWindMapScrub = (forecastHour: number) =>
  trackEvent("wind_map_scrub", { forecast_hour: forecastHour });

// ── Home spot ────────────────────────────────────────────────────────────

export const trackHomeSpotSet = (regionId: string, cameraId: string | null) =>
  trackEvent(
    "home_spot_set",
    cameraId ? { region_id: regionId, camera_id: cameraId } : { region_id: regionId },
  );

export const trackHomeSpotClear = () => trackEvent("home_spot_clear");

// ── Share ────────────────────────────────────────────────────────────────

export const trackShareClick = () => trackEvent("share_click");

export const trackShareSuccess = (method: "native" | "clipboard") =>
  trackEvent("share_success", { method });

export const trackShareCancel = () => trackEvent("share_cancel");

// ── Welcome ──────────────────────────────────────────────────────────────

export const trackWelcomeArrival = (campaign: string) =>
  trackEvent("welcome_arrival", { campaign });

export const trackWelcomeLocateSuccess = (regionId: string, campaign: string) =>
  trackEvent("welcome_locate_success", { region_id: regionId, campaign });

export const trackWelcomeLocateError = (reason: string, campaign: string) =>
  trackEvent("welcome_locate_error", { reason, campaign });

export const trackWelcomeSigninClick = (campaign: string) =>
  trackEvent("welcome_signin_click", { campaign });

export const trackWelcomeSkipClick = (campaign: string) =>
  trackEvent("welcome_skip_click", { campaign });

// ── Engagement prompt ────────────────────────────────────────────────────

export const trackEngagementPromptShown = () => trackEvent("engagement_prompt_shown");

export const trackEngagementPromptDismissed = () =>
  trackEvent("engagement_prompt_dismissed");
